import React, { useState, useEffect } from 'react';

// Collects enquiry details and opens WhatsApp with them pre-filled, so the
// user only has to press Send.
//
// fields: [{ name, label, type, options, required, placeholder, min, max,
//            defaultValue, wide, requiredIfOther }]
//   type: text | number | date | datetime-local | select | textarea
//   min: "today" on a date field blocks past dates
//   wide: span both columns on desktop
//   requiredIfOther: required when any select is set to an "Other..." option
// prefill: values to merge into the form when they change (e.g. a card's
//          "Enquire" button choosing the course)

export const WHATSAPP_NUMBER = "919703491968";

const themes = {
    green: {
        box: "bg-green-50 dark:bg-green-900 border-green-500",
        title: "text-green-800 dark:text-green-200",
        intro: "text-green-700 dark:text-green-300",
        label: "text-green-900 dark:text-green-100",
        button: "bg-green-500 hover:bg-green-600",
        ring: "focus:ring-green-500",
    },
    blue: {
        box: "bg-blue-50 dark:bg-blue-900 border-blue-500",
        title: "text-blue-800 dark:text-blue-200",
        intro: "text-blue-700 dark:text-blue-300",
        label: "text-blue-900 dark:text-blue-100",
        button: "bg-blue-500 hover:bg-blue-600",
        ring: "focus:ring-blue-500",
    },
};

const formatValue = (field, value) => {
    if (field.type === "date") {
        return new Date(`${value}T00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    }
    if (field.type === "datetime-local") {
        return new Date(value).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    }
    return String(value).trim();
};

export const WhatsAppIcon = ({ className = "w-6 h-6 mr-3" }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.886-.001 2.267.651 4.383 1.803 6.166l-1.225 4.469 4.575-1.201zM18.33 14.229c-.301-.15-1.767-.867-2.04-.967-.272-.1-.47-.15-.67.15-.197.299-.771.966-.944 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.299-.018-.462.13-.611.134-.133.297-.347.446-.52.15-.174.199-.299.298-.498.1-.199.05-.374-.025-.524-.075-.15-.67-1.612-.916-2.206-.242-.58-.487-.5-.67-.506-.183-.006-.38-.006-.579-.006-.199 0-.523.074-.797.373-.273.3-.981.957-.981 2.322 0 1.365 1.007 2.699 1.157 2.897.149.199 1.958 3.024 4.793 4.22 2.836 1.196 2.836.797 3.344.746.508-.05 1.767-.717 2.016-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.199-.57-.347z"/></svg>
);

function WhatsAppEnquiryForm({
    id,
    title,
    intro,
    fields,
    greeting,
    closing = "Please get back to me with the next steps. Thanks!",
    submitLabel = "Send on WhatsApp",
    theme = "green",
    prefill,
    className = "mb-12",
}) {
    const t = themes[theme];
    const [values, setValues] = useState(() =>
        Object.fromEntries(fields.map((f) => [f.name, f.defaultValue ?? ""]))
    );
    const [today, setToday] = useState("");

    // Set after mount so the react-snap prerender doesn't bake in the build date
    useEffect(() => setToday(new Date().toISOString().split("T")[0]), []);

    useEffect(() => {
        if (prefill) setValues((v) => ({ ...v, ...prefill }));
    }, [prefill]);

    const otherSelected = fields.some((f) => f.type === "select" && String(values[f.name]).startsWith("Other"));

    const handleSubmit = (e) => {
        e.preventDefault();
        const lines = [greeting, ""];
        fields.forEach((f) => {
            const value = values[f.name];
            if (value !== "" && value !== undefined) lines.push(`*${f.label}:* ${formatValue(f, value)}`);
        });
        lines.push("", closing);
        const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const inputClass = `w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 ${t.ring}`;

    const renderInput = (f) => {
        const common = {
            id: `${id}-${f.name}`,
            className: inputClass,
            value: values[f.name],
            onChange: (e) => setValues({ ...values, [f.name]: e.target.value }),
            required: f.required || (f.requiredIfOther && otherSelected),
            placeholder: f.placeholder,
        };
        if (f.type === "select") {
            return (
                <select {...common}>
                    <option value="" disabled={f.required}>{f.required ? "Select..." : "Select (optional)"}</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            );
        }
        if (f.type === "textarea") return <textarea rows="2" {...common} />;
        const min = f.min === "today" ? today : f.min;
        return <input type={f.type || "text"} min={min} max={f.max} {...common} />;
    };

    return (
        <div id={id} className={`${t.box} border-2 border-dashed rounded-lg p-6 scroll-mt-6 ${className}`}>
            <h2 className={`text-2xl font-bold ${t.title} mb-2 text-center`}>{title}</h2>
            {intro && <p className={`${t.intro} mb-6 text-center`}>{intro}</p>}
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                {fields.map((f) => (
                    <div key={f.name} className={f.wide || f.type === "textarea" ? "md:col-span-2" : ""}>
                        <label className={`block text-sm font-semibold ${t.label} mb-1`} htmlFor={`${id}-${f.name}`}>
                            {f.label}
                            {!f.required && !(f.requiredIfOther && otherSelected) && <span className="font-normal opacity-75"> (optional)</span>}
                            {f.requiredIfOther && otherSelected && <span className="font-normal"> (please tell us about your "Other" choice)</span>}
                        </label>
                        {renderInput(f)}
                    </div>
                ))}
                <div className="md:col-span-2 text-center">
                    <button
                        type="submit"
                        className={`inline-flex items-center justify-center px-8 py-3 rounded-full ${t.button} text-white font-bold text-lg transition duration-200 shadow-md transform hover:scale-105`}
                    >
                        <WhatsAppIcon />
                        {submitLabel}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default WhatsAppEnquiryForm;
